import GenerationWorker from 'worker-loader!./generation.worker'
import { toRaw } from 'vue'
import { LocalCompRegistry } from '@/library/localCompRegistry'
import type { ChipJSON } from './model/types'
import type { VoronoiCompensationData } from './builder/shapes/voronoiCompensation'
import type {
  GenerationWorkerRequest,
  GenerationWorkerResponse,
  LocalCompensationEntries,
} from './workerProtocol'

interface PendingRequest {
  resolve: (buffer: ArrayBuffer) => void
  reject: (error: Error) => void
}

let worker: Worker | null = null
let nextRequestId = 1
let hasCompletedPrimaryStl = false
const pendingRequests = new Map<number, PendingRequest>()

function rejectPending(error: Error): void {
  pendingRequests.forEach(({ reject }) => reject(error))
  pendingRequests.clear()
}

function disposeWorker(reason?: Error): void {
  if (worker) {
    worker.onmessage = null
    worker.onerror = null
    worker.terminate()
    worker = null
  }
  hasCompletedPrimaryStl = false
  if (reason) rejectPending(reason)
}

function ensureWorker(): Worker {
  if (worker) return worker

  const createdWorker = new GenerationWorker()
  createdWorker.onmessage = (event: MessageEvent<GenerationWorkerResponse>) => {
    const response = event.data
    const pending = pendingRequests.get(response.id)
    if (!pending) return
    pendingRequests.delete(response.id)

    if (response.success) {
      pending.resolve(response.buffer)
      return
    }

    const error = new Error(response.error.message)
    error.name = response.error.name
    if (response.error.stack) error.stack = response.error.stack
    pending.reject(error)
  }
  createdWorker.onerror = (event: ErrorEvent) => {
    const error = new Error(event.message || '3D generation worker failed')
    disposeWorker(error)
  }
  worker = createdWorker
  return createdWorker
}

function sendRequest(request: GenerationWorkerRequest): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    pendingRequests.set(request.id, { resolve, reject })
    try {
      // Pinia/Vue can expose cached generation inputs as Proxies. Structured
      // clone rejects Proxy objects, so always send their original targets.
      const cloneableRequest: GenerationWorkerRequest = {
        ...request,
        chipJSON: toRaw(request.chipJSON),
        voronoiCompensationData: request.voronoiCompensationData
          ? toRaw(request.voronoiCompensationData)
          : undefined,
        localCompensations: toRaw(request.localCompensations).map(
          ([elevation, localComp]) => [elevation, localComp]
        ),
      }
      ensureWorker().postMessage(cloneableRequest)
    } catch (error) {
      pendingRequests.delete(request.id)
      reject(error instanceof Error ? error : new Error(String(error)))
    }
  })
}

function startNewStlRequest(): void {
  if (pendingRequests.size > 0) {
    const error = new Error('The previous 3D generation request was superseded')
    error.name = 'AbortError'
    disposeWorker(error)
  }
}

export async function generateStlInWorker(
  chipJSON: ChipJSON,
  voronoiCompensationData?: VoronoiCompensationData[],
  isBinary = false,
  scaleToMm = true,
  internalOnly = false,
  localCompensations: LocalCompensationEntries = LocalCompRegistry.entries()
): Promise<ArrayBuffer> {
  // Terminating the old worker is the only reliable way to interrupt a
  // synchronous OpenCascade boolean or meshing operation.
  startNewStlRequest()
  // A new primary model no longer needs the previous BRep. Recreate the
  // worker to reclaim all WASM/Embind allocations that OCCT made for it.
  if (scaleToMm && hasCompletedPrimaryStl) disposeWorker()

  const result = await sendRequest({
    id: nextRequestId++,
    type: 'generate-stl',
    chipJSON,
    voronoiCompensationData,
    isBinary,
    scaleToMm,
    internalOnly,
    localCompensations,
  })
  if (scaleToMm) hasCompletedPrimaryStl = true
  return result
}

export async function generateStepInWorker(
  chipJSON: ChipJSON,
  voronoiCompensationData?: VoronoiCompensationData[],
  internalOnly = false,
  localCompensations: LocalCompensationEntries = LocalCompRegistry.entries()
): Promise<Blob> {
  const buffer = await sendRequest({
    id: nextRequestId++,
    type: 'generate-step',
    chipJSON,
    voronoiCompensationData,
    internalOnly,
    localCompensations,
  })
  return new Blob([buffer], { type: 'application/step' })
}

export function terminateGenerationWorker(): void {
  const error = new Error('3D generation worker was terminated')
  error.name = 'AbortError'
  disposeWorker(error)
}
